/**
 * Admission QnA State Module
 * Recoil state management for QnA sessions and messages
 */

import { useCallback } from 'react';
import { atom, useRecoilState } from 'recoil';
import * as QnAData from '@/Data/Chat';
import type { QnaSession, QnaMessage, ListQnaSessionsParams, GetQnaMessagesParams, CreateQnaSessionParams } from 'ai-api/types/admission-qna';
import { randomString } from '@/Functions';

// ============ Validation ============

const validateSession = (s: unknown): s is QnaSession => {
    return (
        s !== null &&
        typeof s === 'object' &&
        typeof (s as QnaSession).sessionid === 'string' &&
        typeof (s as QnaSession).userid === 'string'
    );
};

const validateMessage = (m: unknown): m is QnaMessage => {
    return (
        m !== null &&
        typeof m === 'object' &&
        typeof (m as QnaMessage).messageid === 'string' &&
        typeof (m as QnaMessage).sessionid === 'string' &&
        typeof (m as QnaMessage).content === 'string'
    );
};

// ============ Atoms ============

interface QnAState {
    sessions: QnaSession[];
    messages: Record<string, QnaMessage[]>; // sessionid -> messages
    loading: boolean;
    error: string | null;
}

export const qnaState = atom<QnAState>({
    key: 'qnaState' + randomString(4),
    default: {
        sessions: [],
        messages: {},
        loading: false,
        error: null,
    },
});

// ============ Hooks ============

/**
 * Hook for managing QnA sessions
 */
export function useQnASessions() {
    const [state, setState] = useRecoilState(qnaState);
    const { sessions, loading, error } = state;

    const syncSessions = useCallback(async (params?: ListQnaSessionsParams) => {
        setState(prev => ({ ...prev, loading: true, error: null }));

        try {
            const result = await QnAData.listSessions(params);

            if (!result || !Array.isArray(result)) {
                setState(prev => ({ ...prev, loading: false }));
                return;
            }

            const valid = result.filter(validateSession);

            setState(prev => ({
                ...prev,
                sessions: valid,
                loading: false,
            }));

            return valid;
        } catch (e) {
            console.error('Failed to sync QnA sessions:', e);
            setState(prev => ({ ...prev, loading: false, error: 'Failed to sync sessions' }));
        }
    }, [setState]);

    const createSession = useCallback(async (params?: CreateQnaSessionParams) => {
        setState(prev => ({ ...prev, loading: true, error: null }));

        try {
            const session = await QnAData.createSession(params);

            if (!session || !validateSession(session)) {
                setState(prev => ({ ...prev, loading: false, error: 'Failed to create session' }));
                return null;
            }

            setState(prev => ({
                ...prev,
                sessions: [session, ...prev.sessions],
                loading: false,
            }));

            return session;
        } catch (e) {
            console.error('Failed to create QnA session:', e);
            setState(prev => ({ ...prev, loading: false, error: 'Failed to create session' }));
            return null;
        }
    }, [setState]);

    const deleteSession = useCallback(async (sessionid: string) => {
        const success = await QnAData.deleteSession({ sessionid });

        if (success) {
            setState(prev => ({
                ...prev,
                sessions: prev.sessions.filter(s => s.sessionid !== sessionid),
                messages: { ...prev.messages, [sessionid]: [] },
            }));
        }

        return success;
    }, [setState]);

    /** Add a session to state (e.g., from stream auto-creation) */
    const addSessionToState = useCallback((session: QnaSession) => {
        setState(prev => ({
            ...prev,
            sessions: [session, ...prev.sessions.filter(s => s.sessionid !== session.sessionid)],
        }));
    }, [setState]);

    return {
        sessions,
        loading,
        error,
        syncSessions,
        createSession,
        deleteSession,
        addSessionToState,
    };
}

/**
 * Hook for managing QnA messages within a session
 */
export function useQnAMessages(sessionid: string) {
    const [state, setState] = useRecoilState(qnaState);
    const messages = state.messages[sessionid] ?? [];
    const { loading, error } = state;

    const syncMessages = useCallback(async (params?: Omit<GetQnaMessagesParams, 'sessionid'>) => {
        if (!sessionid) return;

        setState(prev => ({ ...prev, loading: true, error: null }));

        try {
            const result = await QnAData.listMessages({ sessionid, ...params });

            if (!result || !Array.isArray(result)) {
                setState(prev => ({ ...prev, loading: false }));
                return;
            }

            const valid = result.filter(validateMessage);

            setState(prev => ({
                ...prev,
                messages: { ...prev.messages, [sessionid]: valid },
                loading: false,
            }));

            return valid;
        } catch (e) {
            console.error('Failed to sync QnA messages:', e);
            setState(prev => ({ ...prev, loading: false, error: 'Failed to sync messages' }));
        }
    }, [sessionid, setState]);

    const addMessage = useCallback((message: QnaMessage) => {
        if (!validateMessage(message)) return;

        setState(prev => ({
            ...prev,
            messages: {
                ...prev.messages,
                [sessionid]: [...(prev.messages[sessionid] ?? []), message]
                    .filter((m, i, arr) => arr.findIndex(x => x.messageid === m.messageid) === i)
                    .sort((a, b) => a.created_at - b.created_at),
            },
        }));
    }, [sessionid, setState]);

    return {
        messages,
        loading,
        error,
        syncMessages,
        addMessage,
    };
}

// Re-export types
export type { QnaSession, QnaMessage } from 'ai-api/types/admission-qna';

// Export stream
export { useQnAStream, qnaStreamState } from './Stream';
